import React, { useState } from 'react';
import Confetti from 'react-confetti';
import { theme } from '../styles/theme';
import Button from './ui/Button';
import { api } from '../lib/api';

interface OnboardingStepsProps {
  isOpen: boolean;
  onComplete: () => void;
  onCreateFirstLink: () => void;
  showConfetti?: boolean;
  orgId: string;
  // True when the lead-magnet flow already created the user's first link.
  // The step 3 row renders as completed and the user only needs to finish
  // steps 1 and 2 to wrap up onboarding.
  initialStep3Complete?: boolean;
}

const OnboardingSteps: React.FC<OnboardingStepsProps> = ({
  isOpen,
  onComplete,
  onCreateFirstLink,
  showConfetti = false,
  orgId,
  initialStep3Complete = false
}) => {
  const [step1Complete, setStep1Complete] = useState(false);
  const [step2Complete, setStep2Complete] = useState(false);
  const [step3Complete] = useState(initialStep3Complete);

  const [webhookFormName, setWebhookFormName] = useState('');
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleStep1Done = () => {
    setStep1Complete(true);
  };

  const handleCreateWebhook = async () => {
    if (!webhookFormName.trim()) {
      setError('Please enter a form name');
      return;
    }

    try {
      setCreatingWebhook(true);
      setError('');
      const response = await api.webhooks.create(webhookFormName.trim());

      if (response.success) {
        setWebhookUrl(response.webhook.webhookUrl);
        setStep2Complete(true);
      } else {
        setError('Failed to create webhook');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create webhook');
    } finally {
      setCreatingWebhook(false);
    }
  };

  const copyWebhookUrl = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl);
      setCopiedWebhook(true);
      setTimeout(() => setCopiedWebhook(false), 2000);
    }
  };

  const handleStep3CreateLink = () => {
    onCreateFirstLink();
  };

  const completedSteps = [step1Complete, step2Complete, step3Complete].filter(Boolean).length;
  const progressPercentage = (completedSteps / 3) * 100;

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
        />
      )}

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '20px'
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '750px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: '32px', textAlign: 'center' }}>
            <h2 style={{
              margin: '0 0 12px 0',
              fontSize: theme.typography.sizes['2xl'],
              fontWeight: theme.typography.weights.bold,
              color: theme.colors.text.primary
            }}>
              Let's Get You Started!
            </h2>
            <p style={{
              margin: 0,
              fontSize: theme.typography.sizes.base,
              color: theme.colors.text.secondary,
              lineHeight: 1.6
            }}>
              Complete these 3 simple steps to start tracking your revenue
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              color: '#DC2626',
              marginBottom: '24px',
              fontSize: theme.typography.sizes.sm
            }}>
              {error}
            </div>
          )}

          {/* Steps */}
          <div style={{ marginBottom: '32px' }}>
            {/* Step 1: Add Hidden Field to Typeform */}
            <div
              style={{
                padding: '24px',
                border: `2px solid ${step1Complete ? '#10B981' : theme.colors.border}`,
                borderRadius: '12px',
                marginBottom: '16px',
                backgroundColor: step1Complete ? '#F0FDF4' : 'white',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: step1Complete ? '#10B981' : theme.colors.primary,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: theme.typography.weights.bold,
                    flexShrink: 0,
                    fontSize: step1Complete ? '20px' : '16px'
                  }}
                >
                  {step1Complete ? '✓' : '1'}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 8px 0',
                    fontSize: theme.typography.sizes.lg,
                    fontWeight: theme.typography.weights.semibold,
                    color: theme.colors.text.primary
                  }}>
                    Add Hidden Field to Your Typeform
                  </h3>
                  <p style={{
                    margin: '0 0 16px 0',
                    fontSize: theme.typography.sizes.sm,
                    color: theme.colors.text.secondary,
                    lineHeight: 1.6
                  }}>
                    Add a hidden field named "trackable_link" to your Typeform. This will allow Revenue Social to track which campaign each booking came from.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <a
                      href="https://revenuesocial.com/guides/how-to-link-typeform"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: theme.colors.primary,
                        color: 'white',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: theme.typography.sizes.sm,
                        fontWeight: theme.typography.weights.medium,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0D9373';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                      }}
                    >
                      Open Guide ↗
                    </a>
                    {!step1Complete && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleStep1Done}
                      >
                        Mark as Done
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Create Webhook */}
            <div
              style={{
                padding: '24px',
                border: `2px solid ${step2Complete ? '#10B981' : theme.colors.border}`,
                borderRadius: '12px',
                marginBottom: '16px',
                backgroundColor: step2Complete ? '#F0FDF4' : 'white',
                opacity: step1Complete ? 1 : 0.5,
                pointerEvents: step1Complete ? 'auto' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: step2Complete ? '#10B981' : theme.colors.primary,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: theme.typography.weights.bold,
                    flexShrink: 0,
                    fontSize: step2Complete ? '20px' : '16px'
                  }}
                >
                  {step2Complete ? '✓' : '2'}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 8px 0',
                    fontSize: theme.typography.sizes.lg,
                    fontWeight: theme.typography.weights.semibold,
                    color: theme.colors.text.primary
                  }}>
                    Create Webhook for Your Typeform
                  </h3>
                  <p style={{
                    margin: '0 0 16px 0',
                    fontSize: theme.typography.sizes.sm,
                    color: theme.colors.text.secondary,
                    lineHeight: 1.6
                  }}>
                    Name your webhook after your Typeform so you can easily identify it. You'll add the generated webhook URL to your Typeform settings.
                  </p>

                  {!step2Complete ? (
                    <div>
                      <input
                        type="text"
                        value={webhookFormName}
                        onChange={(e) => setWebhookFormName(e.target.value)}
                        placeholder="e.g., Sales Call Booking Form"
                        disabled={creatingWebhook}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: '6px',
                          fontSize: theme.typography.sizes.sm,
                          marginBottom: '12px'
                        }}
                      />
                      <Button
                        variant="primary"
                        size="md"
                        onClick={handleCreateWebhook}
                        disabled={creatingWebhook || !step1Complete}
                      >
                        {creatingWebhook ? 'Creating...' : 'Create Webhook'}
                      </Button>
                    </div>
                  ) : (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#F0F9FF',
                      borderRadius: '8px',
                      border: '1px solid #0EA5E9'
                    }}>
                      <p style={{
                        margin: '0 0 8px 0',
                        fontSize: theme.typography.sizes.xs,
                        fontWeight: theme.typography.weights.semibold,
                        color: '#0284C7',
                        textTransform: 'uppercase'
                      }}>
                        Your Webhook URL (add this to Typeform)
                      </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <code style={{
                          flex: 1,
                          fontSize: '12px',
                          color: '#1E293B',
                          wordBreak: 'break-all',
                          fontFamily: 'monospace',
                          backgroundColor: 'white',
                          padding: '8px',
                          borderRadius: '4px'
                        }}>
                          {webhookUrl}
                        </code>
                        <Button
                          variant={copiedWebhook ? 'secondary' : 'primary'}
                          size="sm"
                          onClick={copyWebhookUrl}
                        >
                          {copiedWebhook ? '✓' : 'Copy'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: Create Trackable Link */}
            <div
              style={{
                padding: '24px',
                border: `2px solid ${step3Complete ? '#10B981' : theme.colors.border}`,
                borderRadius: '12px',
                backgroundColor: step3Complete ? '#F0FDF4' : 'white',
                opacity: step3Complete || step2Complete ? 1 : 0.5,
                pointerEvents: step3Complete || step2Complete ? 'auto' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: step3Complete ? '#10B981' : theme.colors.primary,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: theme.typography.weights.bold,
                    flexShrink: 0,
                    fontSize: step3Complete ? '20px' : '16px'
                  }}
                >
                  {step3Complete ? '✓' : '3'}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 8px 0',
                    fontSize: theme.typography.sizes.lg,
                    fontWeight: theme.typography.weights.semibold,
                    color: theme.colors.text.primary
                  }}>
                    {step3Complete ? 'Your First Trackable Link Is Ready' : 'Create Your First Trackable Link'}
                  </h3>
                  <p style={{
                    margin: '0 0 16px 0',
                    fontSize: theme.typography.sizes.sm,
                    color: theme.colors.text.secondary,
                    lineHeight: 1.6
                  }}>
                    {step3Complete
                      ? 'We created your first trackable link from the URL you entered before signing up. Finish steps 1 and 2 above to start tracking bookings.'
                      : 'Create a trackable link for your campaign. When people click this link and submit your Typeform, their booking will be tracked automatically.'}
                  </p>
                  {!step3Complete && (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={handleStep3CreateLink}
                      disabled={!step2Complete}
                    >
                      Create Trackable Link
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            backgroundColor: theme.colors.background,
            borderRadius: '8px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                height: '8px',
                backgroundColor: '#E5E7EB',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  backgroundColor: theme.colors.primary,
                  borderRadius: '4px',
                  width: `${progressPercentage}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
            <span style={{
              fontSize: theme.typography.sizes.sm,
              fontWeight: theme.typography.weights.semibold,
              color: theme.colors.text.secondary,
              whiteSpace: 'nowrap'
            }}>
              {completedSteps} of 3 complete
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingSteps;
